import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackNewSellerPillHover } from '../../tracking/newSellerPillTracking.js';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Badge, type BackgroundColor } from 'dibs-elements/exports/Badge.tsx';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';

import { type PdpSharedNewSellerPill_viewer$key } from './__generated__/PdpSharedNewSellerPill_viewer.graphql.ts';

// styles
import styles from './PdpSharedNewSellerPill.scss';

const viewerFragment = graphql`
    fragment PdpSharedNewSellerPill_viewer on Viewer {
        suppressNewSellerSignals: featureFlag(feature: "suppressNewSellerSignals")
    }
`;

export const PdpSharedNewSellerPill: FC<{
    viewer: PdpSharedNewSellerPill_viewer$key | null;
    backgroundColor?: BackgroundColor;
    unsetTextSize?: boolean;
}> = ({ viewer: viewerRef, backgroundColor, unsetTextSize = false }) => {
    const viewer = useFragment(viewerFragment, viewerRef);

    const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
    const showTooltip = useCallback(() => {
        setIsTooltipOpen(true);
        trackNewSellerPillHover();
    }, []);
    const hideTooltip = useCallback(() => setIsTooltipOpen(false), []);

    if (viewer?.suppressNewSellerSignals) {
        return null;
    }

    return (
        <span
            className={styles.container}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            data-tn="pdp-new-seller-pill"
        >
            <Badge backgroundColor={backgroundColor} type="pill" size="small-heavy">
                <span className={unsetTextSize ? undefined : styles.text}>
                    <FormattedMessage
                        id="PdpSharedNewSellerPill.pill_text"
                        defaultMessage="New to 1stDibs"
                    />
                </span>
            </Badge>

            <Tooltip isVisible={isTooltipOpen} direction="top" type="small">
                <div className={styles.tooltipText}>
                    <FormattedMessage
                        id="PdpSharedNewSellerPill.tooltip"
                        defaultMessage="Joined in the past six months."
                    />
                </div>
            </Tooltip>
        </span>
    );
};
